import React, { useEffect, useState, useCallback } from 'react';
import { Mail, CheckCircle2, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  green: {
    icon: CheckCircle2,
    chipBg: 'bg-emerald-500/10 border-emerald-500/40',
    chipText: 'text-emerald-300',
    dot: 'bg-emerald-400',
  },
  amber: {
    icon: AlertTriangle,
    chipBg: 'bg-amber-500/10 border-amber-500/40',
    chipText: 'text-amber-300',
    dot: 'bg-amber-400',
  },
  red: {
    icon: XCircle,
    chipBg: 'bg-red-500/10 border-red-500/40',
    chipText: 'text-red-300',
    dot: 'bg-red-400',
  },
};

const fmtRel = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
};

export const SendGridHealthPill = ({ getStoredPassword }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const pwd = encodeURIComponent(getStoredPassword?.() || '');
      const r = await fetch(`${API_URL}/api/admin/notifications/sendgrid-health?password=${pwd}`);
      if (r.ok) setData(await r.json());
    } catch { /* silent */ }
    setLoading(false);
  }, [getStoredPassword]);

  useEffect(() => {
    load();
    // Auto-refresh every 60s so admins see quota changes without manual refresh
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  if (!data) return null;

  const status = data.status || 'amber';
  const style = STATUS_STYLES[status] || STATUS_STYLES.amber;
  const Icon = style.icon;
  const pct = data.sends_today != null && data.daily_cap
    ? Math.min(100, Math.round((data.sends_today / data.daily_cap) * 100))
    : null;

  return (
    <div className="relative inline-block" data-testid="sendgrid-health-pill">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${style.chipBg} ${style.chipText} text-[11px] font-medium hover:brightness-110 transition`}
        data-testid={`sendgrid-health-${status}`}
        aria-expanded={open}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${style.dot} ${status === 'red' ? 'animate-pulse' : ''}`} />
        <Mail className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">
          {status === 'green' && 'Email healthy'}
          {status === 'amber' && 'Email nearing cap'}
          {status === 'red' && 'Email blocked'}
        </span>
        <span className="sm:hidden uppercase tracking-wider">{status}</span>
        {pct != null && status !== 'red' && (
          <span className="opacity-60">· {pct}%</span>
        )}
      </button>

      {open && (
        <div
          data-testid="sendgrid-health-popover"
          className="absolute right-0 mt-2 w-80 bg-[#0a1a14] border border-white/15 rounded-lg shadow-2xl p-4 z-50 text-sm"
        >
          <div className="flex items-start gap-2.5 mb-3">
            <div className={`w-8 h-8 rounded-md ${style.chipBg} flex items-center justify-center shrink-0`}>
              <Icon className={`w-4 h-4 ${style.chipText}`} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">
                SendGrid Outbound Email
              </div>
              <div className={`text-sm font-semibold ${style.chipText}`}>
                {data.message || 'Status unknown'}
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); load(); }}
              className="text-white/40 hover:text-white/80 p-1"
              data-testid="sendgrid-health-refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <dl className="space-y-1.5 text-[12px]">
            <div className="flex justify-between gap-3">
              <dt className="text-white/50">API key</dt>
              <dd className={data.authenticated ? 'text-emerald-300' : 'text-red-300'}>
                {data.authenticated ? 'authenticated' : 'rejected'}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-white/50">Sender</dt>
              <dd className="text-white/80 truncate max-w-[180px]" title={data.sender_email}>
                {data.sender_email || '—'}
              </dd>
            </div>
            {data.sends_today != null && (
              <div className="flex justify-between gap-3">
                <dt className="text-white/50">Sends today</dt>
                <dd className="text-white/80 tabular-nums">
                  {data.sends_today} / {data.daily_cap}
                </dd>
              </div>
            )}
            {data.last_dispatch_error && (
              <div className="pt-2 mt-2 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-[0.15em] text-red-300/70 mb-1">
                  Last send error · {fmtRel(data.last_dispatch_error.at)}
                </div>
                <div
                  data-testid="sendgrid-last-error"
                  className="text-[11px] text-white/70 leading-relaxed break-words bg-red-500/5 border border-red-500/20 rounded px-2 py-1.5 max-h-24 overflow-y-auto"
                >
                  {data.last_dispatch_error.reason}
                </div>
              </div>
            )}
          </dl>

          {status !== 'green' && (
            <a
              href="https://app.sendgrid.com/account/billing"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex w-full justify-center items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] hover:text-[#E8C547] py-1.5 border border-[#D4AF37]/30 rounded-md hover:bg-[#D4AF37]/5 transition"
              data-testid="sendgrid-health-billing-link"
            >
              Open SendGrid billing →
            </a>
          )}

          <div className="text-[9px] text-white/30 mt-2 text-right">
            Refreshed {fmtRel(data.checked_at)}
          </div>
        </div>
      )}
    </div>
  );
};

export default SendGridHealthPill;
